// @flow

import React, {useEffect, useRef, useState} from 'react';
import {Grid, IconButton, makeStyles} from '@material-ui/core';
import CustomTextField from '../../../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import TextEditor from '../../../Sections/ProductFeaturesSection/TextEditor/TextEditor';
import {colors} from '../../../../../../assets/styles/colors';
// import IosSwitch from '../../../../../../elements/IosSwitch/IosSwitch';
import {Controller, useFormContext} from 'react-hook-form';
import {Close, DeleteOutline} from '@material-ui/icons';
import SelectImageLayoutModal from './SelectImageLayoutModal';
import {useProductFormContext} from '../../../../context/ProductForm';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import PublishIcon from '@material-ui/icons/Publish';

import type {ProductSectionImageLayout} from '../../../../../../entities';

type Props = {
  idx: number,
  section: {
    id: string,
    title: string,
    content?: string,
    enabled: boolean,
    productSectionImageLayout: ProductSectionImageLayout,
  },
  onRemove: () => void,
  disabled?: boolean,
  handleDragStart: (e: SyntheticDragEvent<HTMLDivElement>, id: string) => void,
  handleDragEnter: (e: SyntheticDragEvent<HTMLDivElement>, id: string) => void,
  isDragging: boolean,
  dragItemId: string,
};

const useStyles = props =>
  makeStyles(theme => ({
    contentSection: {
      display: 'flex',
      borderRadius: '12px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      padding: theme.spacing(2),
      backgroundColor: props.isDragging ? '#EEE5FF' : colors.grey200,
      border: `1px solid ${props.isDragging ? '#7B39FF' : 'transparent'}`,
      gap: theme.spacing(2),
    },
    containerForm: {
      flexGrow: 1,
      backgroundColor: 'white',
      padding: theme.spacing(3),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(2),
      },
    },
    dragIcon: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      alignItems: 'center',
      color: colors.grey600,
    },
    uploadImage: {
      width: '100%',
      heigth: '100%',
      border: `1px dashed ${colors.grey500}`,
      height: '200px',
      borderRadius: 10,
    },
    switchLabel: {
      fontSize: '14px',
      color: colors.grey800,
      fontWeight: 'normal',
      lineHeight: '22px',
    },
    deleteBtnWrap: {
      width: '100%',
      display: 'flex',
      justifyContent: 'flex-end',
    },
    deleteBtn: {
      color: colors.error,
    },
    iconUpload: {
      padding: theme.spacing(2),
      backgroundColor: '#EEE5FF',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    preview: {
      position: 'relative',
      borderRadius: 10,
      border: `1px dashed ${colors.grey500}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: theme.spacing(2),
      '& img': {
        maxWidth: '100%',
        width: '100%',
      },
    },
    removePreview: {
      position: 'absolute',
      top: theme.spacing(1.5),
      right: theme.spacing(1.5),
    },
    textEditorWrap: {
      height: '100%',
      '& .w-md-editor': {
        minHeight: '200px',
        height: '100%!important',
      },
    },
  }));

export default function EditNewSection({
  section,
  idx,
  onRemove,
  handleDragStart,
  handleDragEnter,
  disabled,
  isDragging,
  dragItemId,
}: Props) {
  const isDraggingSelf = dragItemId === section.id;
  const classes = useStyles({isDragging: isDraggingSelf})();

  const itemRef = useRef<HTMLDivElement | null>(null);

  const {
    sectionImages,
    setSectionImages,
    setSectionImageLayoutType,
  } = useProductFormContext();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const {register, errors, control, setValue} = useFormContext();

  // const enabledSection = watch(`productSections.${idx}.enabled`);

  const layoutRegisterName = `productSections.${idx}.productSectionImageLayout`;
  const onImageSave = (data: any, layout: ProductSectionImageLayout) => {
    setValue(layoutRegisterName, layout);

    setSectionImageLayoutType(ps => {
      const aux = [...ps];
      aux[idx] = layout;
      return aux;
    });

    setSectionImages(ps => {
      const aux = [...ps];
      aux[idx] = data;
      return aux;
    });
    setOpenModal(false);
  };

  const handleRemoveImage = () => {
    setValue(layoutRegisterName, null);
    setSectionImages(ps => {
      const aux = [...ps];
      aux[idx] = {
        file: null,
        preview: null,
        imageId: undefined,
      };
      return aux;
    });

    setSectionImageLayoutType(ps => {
      const aux = [...ps];
      aux[idx] = null;
      return aux;
    });
  };

  // useEffect(() => {
  //   register(layoutRegisterName);
  // }, [idx, layoutRegisterName, register]);

  useEffect(() => {
    if (section.productSectionImageLayout)
      setValue(layoutRegisterName, section.productSectionImageLayout);
  }, [idx, layoutRegisterName, section.productSectionImageLayout, setValue]);

  const hasImagePreview = !!sectionImages[idx]?.preview;

  const buildImagePreview = () => {
    const preview = sectionImages[idx].preview;
    return (
      <div className={classes.preview}>
        <IconButton
          size="small"
          className={classes.removePreview}
          onClick={handleRemoveImage}
        >
          <Close fontSize="small" />
        </IconButton>
        <img src={preview} alt="preview" />
      </div>
    );
  };

  const onStart = e => {
    if (!disabled && dragItemId !== section.id)
      handleDragStart({...e, target: itemRef.current}, section.id);
  };

  const onEnter = e => {
    if (!disabled && dragItemId !== section.id)
      handleDragEnter({...e, target: itemRef.current}, section.id);
  };

  return (
    <div
      className={classes.contentSection}
      ref={itemRef}
      draggable={!disabled}
      onDragStart={onStart}
      onDragEnter={isDragging ? onEnter : null}
    >
      {openModal && (
        <SelectImageLayoutModal
          open={openModal}
          handleClose={() => setOpenModal(false)}
          onImageSave={onImageSave}
        />
      )}
      <div className={classes.dragIcon}>
        <DragIndicatorIcon />
      </div>
      <div className={classes.containerForm}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomTextField
              label="Escribe aquí la descripción"
              name={`productSections.${idx}.title`}
              defaultValue={section.title}
              error={!!errors.productSections && !!errors.productSections[idx]}
              helperText={
                errors.productSections &&
                errors.productSections[idx] &&
                errors.productSections[idx].title
                  ? errors.productSections[idx].title.message
                  : ''
              }
              type="text"
              // disabled={disabled}
              inputRef={register({
                required: 'Campo obligatorio',
              })}
            />
          </Grid>
          {/* <Grid item xs={12} md={2}>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <FormControlLabel
                classes={{
                  label: classes.switchLabel,
                }}
                control={
                  <Controller
                    name={`productSections.${idx}.enabled`}
                    defaultValue={section.enabled}
                    control={control}
                    render={({
                      onChange,
                      onBlur,
                      value,
                      defaultValue,
                      name,
                      ref,
                    }) => (
                      <IosSwitch
                        checked={value}
                        defaultChecked={defaultValue}
                        className={classes.switchContainer}
                        inputRef={ref}
                        onChange={e => {
                          onChange(e.target.checked);
                        }}
                      />
                    )}
                  />
                }
                labelPlacement="start"
                label={enabledSection ? 'Activado' : 'Desactivado'}
              />
            </div>
          </Grid> */}
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={3}>
                {hasImagePreview ? (
                  buildImagePreview()
                ) : (
                  <CustomButton
                    fullWidth
                    className={classes.uploadImage}
                    onClick={() => setOpenModal(true)}
                  >
                    <div className={classes.iconUpload}>
                      <PublishIcon />
                    </div>
                  </CustomButton>
                )}
              </Grid>
              <Grid item xs={12} md={9}>
                <Controller
                  name={`productSections.${idx}.content`}
                  defaultValue={section.content}
                  control={control}
                  render={({
                    onChange,
                    onBlur,
                    value,
                    defaultValue,
                    name,
                    ref,
                  }) => (
                    <TextEditor
                      text={value}
                      handleDescription={onChange}
                      placeholder="Descripción de tu producto"
                      className={classes.textEditorWrap}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.deleteBtnWrap}>
              <CustomButton
                className={classes.deleteBtn}
                onClick={onRemove}
                startIcon={<DeleteOutline />}
              >
                Eliminar
              </CustomButton>
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}
